import Text from 'components/_UI/Text'
import Fieldset from 'components/_UI/Fieldset'
import Switch from 'components/_UI/Switch'
import { useEvent } from 'context/events/Event'
import React, { useEffect, useState } from 'react'
import { Column } from 'styled/alignment/Column'
import colors from 'theme/colors'
import eventTypes from 'theme/eventTypes'
import { Settings, DollarSign, Shield, Clipboard, Music } from 'lucide-react'
import styled from 'styled-components'

export default function ExtraInfo({ register, errors, edit = false, onChangeSpotifyUrl }) {
    const { event, setEvent } = useEvent();

    const [isPaid, setIsPaid] = useState(false)
    const [isAdulthood, setIsAdulthood] = useState(false)
    const [withTickets, setWithTickets] = useState(false)
    const [spotifyUrl, setSpotifyUrl] = useState('')

    const canEditIsPaid = () => {
        try {
            if (!edit) {
                return true
            }

            const isEventDraft = event.draft === 1
            if (isEventDraft) {
                return true
            }

            const dateItem = event.dates[0]
            const eventStartDate = new Date(`${dateItem.date}T${dateItem.start}`);
            const eventStarted = eventStartDate < new Date()

            if (!eventStarted) {
                return true
            }

            return false
        } catch (error) {
            return true
        }
    }

    useEffect(() => {
        if (edit) {
            setEvent({
                ...event,
                has_tickets: edit?.has_tickets,
                adulthood: edit?.adulthood,
                classification_id: edit?.classification?.id,
            })
            setIsPaid(edit?.classification?.id == eventTypes.paid.id)
            setIsAdulthood(edit?.adulthood)
            setWithTickets(edit?.has_tickets)
            setSpotifyUrl(edit?.spotify_playlist_url || '')
            onChangeSpotifyUrl(edit?.spotify_playlist_url || '')
            return
        }

        setEvent({
            ...event,
            adulthood: false,
            classification_id: eventTypes.free.id
        })

        setIsPaid(false)
        setIsAdulthood(false)
    }, [])

    return (
        <Column>
            <SectionHeader>
                <SectionIcon><Settings size={18} color={colors.occasionalPurple} /></SectionIcon>
                <Text type='bold' color={colors.nightDark}>
                    Configurações do evento
                </Text>
            </SectionHeader>

            <ToggleCardsGrid>
                <ToggleCard
                    active={isPaid}
                    disabled={!canEditIsPaid()}
                    onClick={() => {
                        if (!canEditIsPaid()) return
                        const next = !isPaid
                        setEvent({
                            ...event,
                            classification_id: next ? eventTypes.paid.id : eventTypes.free.id
                        })
                        setIsPaid(next)
                    }}
                >
                    <ToggleCardIcon>
                        <DollarSign size={20} color={isPaid ? colors.occasionalPurple : colors.night} />
                    </ToggleCardIcon>
                    <ToggleCardContent>
                        <ToggleCardTitle>Evento pago</ToggleCardTitle>
                        <ToggleCardDescription>
                            {isPaid
                                ? 'O evento requer pagamento para participar.'
                                : 'O evento é gratuito para todos.'
                            }
                        </ToggleCardDescription>
                    </ToggleCardContent>
                    <div onClick={e => e.stopPropagation()}>
                        <Switch
                            checked={isPaid}
                            onChange={(e) => {
                                setEvent({
                                    ...event,
                                    classification_id: e ? eventTypes.paid.id : eventTypes.free.id
                                })
                                setIsPaid(e)
                            }}
                            disabled={!canEditIsPaid()}
                        />
                    </div>
                </ToggleCard>

                <ToggleCard
                    active={isAdulthood}
                    onClick={() => {
                        const next = !isAdulthood
                        setEvent({
                            ...event,
                            adulthood: !next ? 0 : 1
                        })
                        setIsAdulthood(next)
                    }}
                >
                    <ToggleCardIcon>
                        <Shield size={20} color={isAdulthood ? colors.occasionalPurple : colors.night} />
                    </ToggleCardIcon>
                    <ToggleCardContent>
                        <ToggleCardTitle>Classificação 18+</ToggleCardTitle>
                        <ToggleCardDescription>
                            {isAdulthood
                                ? 'Restrito para maiores de 18 anos.'
                                : 'Sem restrição de idade.'
                            }
                        </ToggleCardDescription>
                    </ToggleCardContent>
                    <div onClick={e => e.stopPropagation()}>
                        <Switch
                            checked={isAdulthood}
                            onChange={(e) => {
                                setEvent({
                                    ...event,
                                    adulthood: !e ? 0 : 1
                                })
                                setIsAdulthood(e)
                            }}
                        />
                    </div>
                </ToggleCard>

                <ToggleCard
                    active={withTickets}
                    onClick={() => {
                        const next = !withTickets
                        setWithTickets(next)
                        setEvent({ ...event, has_tickets: next })
                    }}
                >
                    <ToggleCardIcon>
                        <Clipboard size={20} color={withTickets ? colors.occasionalPurple : colors.night} />
                    </ToggleCardIcon>
                    <ToggleCardContent>
                        <ToggleCardTitle>Criar ingressos</ToggleCardTitle>
                        <ToggleCardDescription>
                            {withTickets
                                ? 'Ingressos serão criados para este evento.'
                                : 'Habilite para criar ingressos e controlar a venda.'
                            }
                        </ToggleCardDescription>
                    </ToggleCardContent>
                    <div onClick={e => e.stopPropagation()}>
                        <Switch
                            checked={withTickets}
                            onChange={(e) => {
                                setWithTickets(e)
                                setEvent({ ...event, has_tickets: e })
                            }}
                        />
                    </div>
                </ToggleCard>
            </ToggleCardsGrid>

            <SpotifySection>
                <SectionHeader>
                    <SectionIcon><Music size={18} color={colors.occasionalPurple} /></SectionIcon>
                    <Text type='bold' color={colors.nightDark}>
                        Playlist do Spotify
                    </Text>
                </SectionHeader>
                <Text color={colors.night} name='small' style={{ marginBottom: 8 }}>
                    Cole o link de uma playlist do Spotify para exibir no seu evento. (opcional)
                </Text>
                <Fieldset
                    layout='squired'
                    label='URL da playlist'
                    placeholder='https://open.spotify.com/playlist/...'
                    value={spotifyUrl}
                    syncProp
                    onChangeValue={(value) => {
                        setSpotifyUrl(value)
                        onChangeSpotifyUrl(value)
                    }}
                />
            </SpotifySection>
        </Column>
    )
}

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
`;

const SectionIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #F3EAFF;
    flex-shrink: 0;
`;

const ToggleCardsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ToggleCard = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 10px;
    border: 1.5px solid ${({ active }) => active ? colors.occasionalPurple + '40' : colors.neutralDark};
    background: ${({ active }) => active ? '#FAFAFF' : colors.neutralLight};
    transition: all .2s ease;
    opacity: ${({ disabled }) => disabled ? 0.6 : 1};
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};

    /* Constrain the Switch (StyledSwitch extends Row with width:100%) */
    & > div:last-child {
        width: auto;
        flex-shrink: 0;
    }

    &:hover {
        border-color: ${({ active, disabled }) => disabled ? colors.neutralDark : active ? colors.occasionalPurple + '60' : colors.nightLight};
    }
`;

const ToggleCardIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const ToggleCardContent = styled.div`
    flex: 1;
    min-width: 0;
    overflow: hidden;
`;

const ToggleCardTitle = styled.p`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
    line-height: 1.3;
`;

const ToggleCardDescription = styled.p`
    font-size: 11px;
    color: ${colors.night};
    margin: 2px 0 0 0;
    line-height: 1.4;
`;

const SpotifySection = styled.div`
    margin-top: 20px;
`;
